import React from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  HotelPaymentMethod,
  HotelPaymentMethodsResponse,
  PaymentMethodPreference,
} from "@/services/interfaces/IBillingSettingsService";

type PaymentMethodsSectionProps = {
  data?: HotelPaymentMethodsResponse;
  isLoading: boolean;
  isError: boolean;
  isUpdating: boolean;
  disabled?: boolean;
  updateError?: string;
  onRetry: () => void;
  onPreferenceChange: (
    method: HotelPaymentMethod,
    preference: Exclude<PaymentMethodPreference, null>,
  ) => void;
};

const paymentMethodPreferences: Exclude<PaymentMethodPreference, null>[] = [
  "none",
  "on",
  "off",
];

const isPaymentMethodPreference = (
  value: string,
): value is Exclude<PaymentMethodPreference, null> =>
  paymentMethodPreferences.some((preference) => preference === value);

const formatMethodName = (code: string) =>
  code
    .split(/[_-]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getPreferenceValue = (
  method: HotelPaymentMethod,
): Exclude<PaymentMethodPreference, null> => method.preference ?? "none";

const isStripeLocked = (method: HotelPaymentMethod) =>
  method.overridable === false;

const hasHotelOverride = (method: HotelPaymentMethod) => {
  const preference = getPreferenceValue(method);

  return preference === "on" || preference === "off";
};

const isPreferenceOptionDisabled = (
  method: HotelPaymentMethod,
  preference: Exclude<PaymentMethodPreference, null>,
) => isStripeLocked(method) && preference !== "none";

const getErrorMessage = (
  method: HotelPaymentMethod,
  t: (key: string) => string,
) => {
  if (isStripeLocked(method) && hasHotelOverride(method)) {
    return t("billing.paymentMethodsResetToDefaultHint");
  }

  if (isStripeLocked(method)) {
    return t("billing.paymentMethodsManagedByStripe");
  }

  return null;
};

export function PaymentMethodsSection({
  data,
  isLoading,
  isError,
  isUpdating,
  disabled = false,
  updateError,
  onRetry,
  onPreferenceChange,
}: PaymentMethodsSectionProps) {
  const { t } = useTranslation();
  const columns: Column<HotelPaymentMethod>[] = [
    {
      key: "name",
      header: t("billing.paymentMethod"),
      render: (_, method) => {
        const hint = getErrorMessage(method, t);

        return (
          <div className="flex flex-col gap-1 whitespace-normal">
            <div className="font-medium text-black/96">
              {formatMethodName(method.code)}
            </div>
            {hint ? <div className="text-xs text-black/45">{hint}</div> : null}
          </div>
        );
      },
    },
    {
      key: "code",
      header: t("billing.code"),
      render: (value: string) => (
        <code className="bg-secondary-badge rounded-md px-2 py-1 text-xs text-black/65">
          {value}
        </code>
      ),
    },
    {
      key: "available",
      header: t("billing.available"),
      render: (value: boolean) => (
        <Badge variant={value ? "status_done" : "new_secondary"}>
          {value ? t("billing.available") : t("billing.unavailable")}
        </Badge>
      ),
    },
    {
      key: "enabled",
      header: t("billing.enabledLabel"),
      render: (value: boolean) => (
        <Badge variant={value ? "verified" : "new_secondary"}>
          {value ? t("billing.enabledState") : t("billing.disabledState")}
        </Badge>
      ),
    },
    {
      key: "overridable",
      header: t("billing.overridable"),
      render: (value: boolean | null) => (
        <Badge variant={value === false ? "new_secondary" : "new_outline"}>
          {value === false ? t("billing.no") : t("billing.yes")}
        </Badge>
      ),
    },
    {
      key: "action",
      header: t("billing.paymentMethodPreference"),
      className: "text-right",
      render: (_, method) => {
        const isDisabled =
          disabled ||
          isUpdating ||
          (isStripeLocked(method) && !hasHotelOverride(method));

        return (
          <div className="flex justify-end">
            <Select
              value={getPreferenceValue(method)}
              disabled={isDisabled}
              onValueChange={(value) => {
                if (isPaymentMethodPreference(value)) {
                  onPreferenceChange(method, value);
                }
              }}
            >
              <SelectTrigger
                size="sm"
                className="min-w-[180px] justify-between"
                aria-label={t("billing.changePaymentMethodPreference", {
                  method: method.code,
                })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {paymentMethodPreferences.map((preference) => (
                    <SelectItem
                      key={preference}
                      value={preference}
                      disabled={isPreferenceOptionDisabled(method, preference)}
                    >
                      {t(`billing.paymentMethodPreferences.${preference}`)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        );
      },
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <div className="border-base-border flex flex-col gap-1 border-b pb-4">
        <h2 className="text-base font-semibold text-black/96">
          {t("billing.paymentMethodsTitle")}
        </h2>
        <p className="text-sm text-black/65">
          {t("billing.paymentMethodsDescription")}
        </p>
      </div>

      {isLoading ? (
        <div className="text-sm text-black/45">
          {t("billing.loadingPaymentMethods")}
        </div>
      ) : null}

      {!isLoading && isError ? (
        <div className="border-custom-destructive-dark/40 bg-custom-destructive-dark/5 text-custom-destructive-dark flex flex-col items-start gap-3 rounded-xl border p-4 text-sm">
          <div>{t("billing.failedToLoadPaymentMethods")}</div>
          <Button
            type="button"
            variant="new_outline"
            size="sm"
            onClick={onRetry}
          >
            {t("billing.retry")}
          </Button>
        </div>
      ) : null}

      {!isLoading && !isError && data ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant="new_secondary">
              {t("billing.configurationName")}: {data.configurationName}
            </Badge>
            <Badge variant="new_secondary">
              {t("billing.environment")}:{" "}
              {data.livemode ? t("billing.liveMode") : t("billing.testMode")}
            </Badge>
          </div>

          <div className="border-base-border rounded-xl border">
            <DataTable
              data={data.methods}
              columns={columns}
              withPagination={false}
              emptyMessage={t("billing.noPaymentMethods")}
              columnHeaderClassName="text-[10px] font-medium text-neutral-500"
            />
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-black/45">
            <span>{t("billing.paymentMethodsUnavailableHint")}</span>
            <span>{t("billing.paymentMethodsManagedByStripe")}</span>
          </div>

          {updateError ? (
            <div className="text-custom-destructive-dark text-sm">
              {updateError}
            </div>
          ) : null}

          {isUpdating ? (
            <div className="text-sm text-black/45">
              {t("billing.updatingPaymentMethod")}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
